import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, computed, AfterViewInit, NgZone, effect, untracked, OnDestroy } from '@angular/core';
import { SidebarService } from '@shared/services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { UsuarioService } from '@shared/services/usuario/usuario.service';
import { BadgeComponent } from '@shared/components/ui/badge/badge.component';
import { PermissionService } from '@shared/services/permission/permission.service';
import { CompanyContextService } from '@pages/empresas/services/company-context.service';
import { SidebarDTO } from '@models/sidebar.model';

@Component({
  selector: 'app-sidebar-menu',
  imports: [
    CommonModule,
    RouterModule,
    BadgeComponent,
    NgOptimizedImage
  ],
  templateUrl: './app-sidebar-menu.component.html',
})
export class AppSidebarMenuComponent implements OnDestroy {
  perfilUsuario = computed(() => this.usuarioService.perfil());

  imgLogoLight = 'images/logo/smartpacking_menu.png';
  imgLogoDark  = 'images/logo/smartpacking_menu_dark.png';
  imgLogoIcon  = 'images/logo/smartpacking_icono.png';

  openSubmenu: string | null = null;
  subMenuHeights: Record<string, number> = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  /**
   * Computed signal que indica si el menú del header está abierto
   * Usado para ajustar dinámicamente la posición del sidebar
   */
  isHeaderMenuOpen = computed(() => this.sidebarService.headerMenuOpen());

  private subscription: Subscription = new Subscription();
  sidebarData: SidebarDTO | null = null;
  private lastCompanyId: number | null = null;
  private lastRoleId: number | null = null;

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private usuarioService: UsuarioService,
    private permissionService: PermissionService,
    private companyContext: CompanyContextService,
    private ngZone: NgZone
  ) {
    // Cargar sidebar cuando cambie el perfil O la empresa seleccionada.
    // Solo limpia caché si el companyId o roleId cambió respecto a la carga anterior,
    // evitando HTTP duplicados en la carga inicial (F5).
    effect(() => {
      const perfil = this.usuarioService.perfil();
      const companyId = this.companyContext.companyId();
      if (perfil?.role?.id && companyId !== null) {
        const roleId = perfil.role.id;
        // untracked: clearSidebarCache lee/escribe el signal sidebarCache.
        // Sin untracked, el effect se registraría como dependiente de ese signal
        // y se re-dispararía en bucle infinito.
        untracked(() => {
          const changed = companyId !== this.lastCompanyId || roleId !== this.lastRoleId;
          if (changed) {
            this.permissionService.clearSidebarCache(roleId);
            this.lastCompanyId = companyId;
            this.lastRoleId = roleId;
          }
          this.loadModuleToSidebar();
        });
      }
    });

    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            this.cdr.detectChanges();
          }
        }
      )
    );

    // Suscribirse a solicitudes de expansión del sidebar
    this.subscription.add(
      this.sidebarService.expandForUrl$.subscribe(url => {
        this.setActiveMenuFromRoute(url);
      })
    );

    this.setActiveMenuFromRoute(this.router.url);

    // Listener para detectar cambios de tamaño de ventana y actualizar estilos del sidebar
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('resize', this.onWindowResize);
    });
  }

  ngAfterViewInit() {
    this.subMenuRefs.changes.subscribe(() => {
      this.updateSubmenuHeights();
    });

    this.updateSubmenuHeights();
  }

  updateSubmenuHeights() {
    if (!this.openSubmenu) return;
    const container = document.getElementById(this.openSubmenu);
    if (!container) return;
    // NO usar container.scrollHeight para calcular la altura cuando el contenido se reduce.
    //
    // Por definicion del DOM: scrollHeight = max(clientHeight, contentHeight).
    // Cuando el <div> tiene height:250px (via ngStyle) y el contenido se reduce a 200px,
    // scrollHeight devuelve 250 (el valor antiguo), por lo que el height nunca se actualiza.
    // Esto afecta al caso "quitar permiso" (contraccion), no al caso "dar permiso"
    // (expansion), donde contentHeight > clientHeight y scrollHeight si refleja el cambio.
    //
    // Solucion: medir el <ul> hijo directo. Al carecer de height fija, su offsetHeight
    // siempre refleja la altura real del contenido (suma de <li> renderizados).
    // Se suma el margin-top del <ul> (clase mt-2) para preservar el espaciado superior.
    const innerList = container.querySelector<HTMLElement>(':scope > ul');
    if (!innerList) return;
    const marginTop = parseFloat(getComputedStyle(innerList).marginTop) || 0;
    this.subMenuHeights[this.openSubmenu] = innerList.offsetHeight + marginTop;
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    // Limpiar listener de resize
    window.removeEventListener('resize', this.onWindowResize);
  }

  /**
   * Maneja el evento resize de la ventana para actualizar los estilos del sidebar
   * Se ejecuta fuera de la zona de Angular para mejor performance
   */
  private onWindowResize = () => {
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  };

  isActive(path: string): boolean {
    return this.router.isActive(path, { paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' });
  }

  // pa generar IDs únicos de grupo y categoría
  getCategoryId(groupId: number, categoryId: number): string {
    return `group-${groupId}-category-${categoryId}`;
  }

  toggleSubmenu(key: string) {
    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      return;
    }
    this.openSubmenu = key;

    setTimeout(() => {
      const el = document.getElementById(key);
      if (el) {
        this.subMenuHeights[key] = el.scrollHeight;
      }
    }, 0);
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    if (!this.sidebarData) return;

    // Buscar la categoría que contiene el módulo activo
    this.sidebarData.groups.forEach(group => {
      group.categories.forEach(category => {
        const hasActiveModule = category.modules.some(module =>  this.router.isActive(module.url, {
          paths: 'subset',
          queryParams: 'ignored',
          fragment: 'ignored',
          matrixParams: 'ignored',
        }));
        
        if (hasActiveModule) {
          const categoryId = this.getCategoryId(group.id, category.id);
          this.openSubmenu = categoryId;
          
          setTimeout(() => {
            this.cdr.detectChanges();
            this.updateSubmenuHeights();
          }, 0);
        }
      });
    });
  }

  /**
   * Calcula los estilos dinámicos del sidebar según el tamaño de pantalla
   * En pantallas >= 1280px (xl): posición normal (top: 0, height: 100vh)
   * En pantallas < 1280px: Se ajusta cuando el menú del header está abierto
   */
  getSidebarStyle() {
    const isXlScreen = window.innerWidth >= 1280;
    
    // En pantallas grandes (xl+), mantener posición normal siempre
    if (isXlScreen) {
      return {};
    }
    
    // En pantallas pequeñas, ajustar cuando el menú del header está abierto
    if (this.isHeaderMenuOpen()) {
      return {
        'top': 'var(--header-expanded-height, 140px)',
        'height': 'calc(100vh - var(--header-expanded-height, 140px))'
      };
    }
    
    return {
      'top': '0',
      'height': '100vh'
    };
  }

  onSubmenuClick() {
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }  

  loadModuleToSidebar() {
    const perfil = this.perfilUsuario();
    const roleId = perfil?.role?.id;

    if (roleId) {
      this.permissionService.getSidebarForRoleCached(roleId)
        .subscribe({
          next: (data) => {
            this.sidebarData = data;
            this.cdr.detectChanges();
            this.setActiveMenuFromRoute(this.router.url);
            // Recalcular la altura del submenu abierto despues de actualizar sidebarData.
            // Se necesita porque:
            //   1. subMenuHeights guarda la altura calculada cuando se abrio el submenu.
            //   2. Si la lista de modulos cambia (permisos cambiados), el contenido del
            //      DOM cambia pero subMenuHeights mantiene el valor anterior.
            //   3. setActiveMenuFromRoute solo recalcula si la ruta actual esta en el sidebar;
            //      si el usuario esta en una pagina fuera del sidebar (ej. /permisos-editar/123),
            //      updateSubmenuHeights nunca se llama desde ahi.
            // El setTimeout garantiza que el event loop procesa el render de detectChanges
            // antes de que leamos scrollHeight, asegurando que el DOM tiene la nueva lista.
            // scrollHeight sobre overflow:hidden retorna la altura intrinseca del contenido,
            // independientemente del valor CSS de height, por lo que mide correctamente
            // los nuevos modulos sin necesidad de resetear subMenuHeights a cero.
            setTimeout(() => this.updateSubmenuHeights(), 0);
          },
          error: (err) => console.error('Error loading sidebar:', err)
        });
    }
  }
}